<template>
  <div
    ref="root"
    :role="type === 'error' ? 'alert' : 'status'"
    :data-notification-type="type"
    class="pointer-events-auto relative flex min-h-12 w-full items-center gap-2.5 overflow-hidden rounded-xl bg-slate-900 py-1.5 pr-1.5 pl-3.5 text-[13px] text-[#e0def4] shadow-popover dark:border dark:border-[#524f67]"
    @mouseenter="hovered = true"
    @mouseleave="hovered = false"
    @focusin="focused = true"
    @focusout="onFocusOut">
    <span
      v-if="life"
      aria-hidden="true"
      class="notification-life absolute inset-y-0 right-0 w-full origin-right bg-white/[.07]"
      :style="{ animationDuration: `${life}ms`, animationPlayState: paused ? 'paused' : 'running' }" />
    <span class="relative flex shrink-0">
      <slot name="icon">
        <component :is="ICONS[type].icon" class="size-[18px]" :class="ICONS[type].color" aria-hidden="true" />
      </slot>
    </span>
    <div class="relative min-w-0 flex-1 py-1.5 leading-5 break-words">
      <p :class="{ 'font-medium': detail }">{{ message }}</p>
      <p v-if="detail" class="opacity-70">{{ detail }}</p>
    </div>
    <button
      v-for="action in actions"
      :key="action.label"
      type="button"
      class="relative h-[34px] shrink-0 cursor-pointer rounded-lg border border-white/[.14] bg-white/[.06] px-3 font-medium hover:bg-white/10"
      @click="action.onClick()">
      {{ action.label }}
    </button>
    <button
      v-if="dismissible"
      type="button"
      :aria-label="dismissLabel"
      class="relative flex size-[34px] shrink-0 cursor-pointer items-center justify-center rounded-lg opacity-70 hover:bg-white/10 hover:opacity-100"
      @click="emit('dismiss')">
      <XMarkIcon class="size-4" aria-hidden="true" />
    </button>
  </div>
</template>

<script setup lang="ts">
import {
  CheckCircleIcon,
  ExclamationCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  XMarkIcon
} from '@heroicons/vue/24/outline'
import type { NotificationAction, NotificationType } from '@dashboard/stores/notificationStore'
import { computed, onBeforeUnmount, onMounted, ref, useTemplateRef, watch } from 'vue'
import type { Component } from 'vue'

const props = withDefaults(
  defineProps<{
    message: string
    type?: NotificationType
    detail?: string
    /** Milliseconds until the card dismisses itself. Without it the card stays until dismissed. */
    life?: number
    actions?: readonly NotificationAction[]
    dismissible?: boolean
    dismissLabel?: string
    /** Pauses the life timer while the stack is stepped aside for a bar menu. */
    held?: boolean
  }>(),
  { type: 'info', dismissible: true, dismissLabel: 'Dismiss notification', actions: () => [] }
)
const emit = defineEmits<{ dismiss: [] }>()

const ICONS: Record<NotificationType, { icon: Component; color: string }> = {
  success: { icon: CheckCircleIcon, color: 'text-emerald-400' },
  error: { icon: ExclamationCircleIcon, color: 'text-red-300' },
  warn: { icon: ExclamationTriangleIcon, color: 'text-amber-300' },
  info: { icon: InformationCircleIcon, color: 'text-sky-300' }
}

// The life timer pauses while the card is hovered, focused or held hidden by
// the stack, and the background fill pauses with it, so it always shows the
// time left.
const root = useTemplateRef<HTMLElement>('root')
const hovered = ref(false)
const focused = ref(false)
const paused = computed(() => hovered.value || focused.value || props.held)
let remaining = 0
let startedAt = 0
let timer: ReturnType<typeof setTimeout> | undefined

function run() {
  startedAt = Date.now()
  timer = setTimeout(() => emit('dismiss'), remaining)
}
function hold() {
  clearTimeout(timer)
  remaining -= Date.now() - startedAt
}
function onFocusOut(event: FocusEvent) {
  focused.value = event.relatedTarget instanceof Node && Boolean(root.value?.contains(event.relatedTarget))
}

onMounted(() => {
  if (props.life) {
    remaining = props.life
    if (!paused.value) {
      run()
    }
  }
})
watch(paused, (isPaused) => {
  if (!props.life) {
    return
  }
  if (isPaused) {
    hold()
  } else {
    run()
  }
})
onBeforeUnmount(() => clearTimeout(timer))
</script>

<style scoped>
.notification-life {
  animation-name: notification-life;
  animation-timing-function: linear;
  animation-fill-mode: forwards;
}
@keyframes notification-life {
  from {
    transform: scaleX(1);
  }
  to {
    transform: scaleX(0);
  }
}
</style>
